import * as Yup from "yup";
import i18next from "../../i18n";
import { AutocompleteFieldValue } from "../carbon/Form/AutocompleteField";

/**
 * Values `NewSiteForm` collects. `address` holds the `{address, googlePlaceId}` pair
 * `AutocompleteField` commits as ONE RHF value (see that component's docstring) — never the two
 * as separate top-level fields.
 */
export type NewSiteFormValues = {
  internalCode: string;
  name: string;
  address: AutocompleteFieldValue;
  country: string;
  comment?: string;
};

/**
 * Validation for the create-site form (`POST /site`), extracted out of `utils/validations.ts`
 * into its own module — the same move `newUserValidationSchema.ts` made for Users (Task 11) — and
 * rebuilt for `AutocompleteField`'s composite value.
 *
 * `utils/validations.ts` still exports its OWN `newSiteValidationSchema` (the pre-Carbon shape:
 * `internalCode`/`name`/`country` only, no `address` rule at all — that form validated the
 * selected place by hand, outside Yup). It is NOT deleted here, unlike `editSiteValidationSchema`
 * — `containers/OrderByRequest/InlineCreateSiteModal.tsx` still imports it and still submits
 * through the old hand-rolled `register`/`InputField` shape, the same "different, unmigrated,
 * untested flow" `AutocompleteField.tsx`'s own docstring documents for `SiteAutocomplete`. This
 * module is what `NewSiteForm.tsx` (this task's rebuild) uses instead.
 *
 * Fields kept verbatim from the pre-Carbon schema: `internalCode`, `name`, `country`, all
 * `.required()`. `comment` was never validated by Yup before (the old form tracked it as raw
 * `useState`, entirely outside `react-hook-form`) and stays unvalidated here too — genuinely
 * optional, not a rule this task is choosing to relax.
 *
 * `address` is new: the pre-Carbon form blocked submission by hand
 * (`if (!address) { setAddress("sites.errorPlace"); return; }` in the old `NewSiteForm.tsx`) —
 * a real requirement, just enforced outside Yup. This is that same rule expressed as the single
 * `.test()` `AutocompleteField.test.tsx` documents and this task's brief points at verbatim: one
 * error at the field's own path (`address`), not RHF's per-subfield nesting a `Yup.object` with
 * per-key `.required()` rules would otherwise produce for a field that renders exactly one error
 * slot. Only `address` (the human-readable string) is checked — `googlePlaceId` is deliberately
 * NOT required, because a real Google selection is not the only way this field can be filled
 * (free-typed text without ever choosing a suggestion still commits a non-empty `address` with an
 * empty `googlePlaceId` — see `AutocompleteField.tsx`'s `onChange`), and because requiring it
 * would make an already-selected address on an EDIT form (this schema's sibling,
 * `editSiteValidationSchema`) impossible to keep unless the user re-picks it through the widget —
 * exactly the forced re-select this task's brief exists to remove.
 */
export const newSiteValidationSchema = Yup.object().shape({
  internalCode: Yup.string().required(i18next.t("validations.internalCode")),
  name: Yup.string().required(i18next.t("validations.siteName")),
  address: Yup.object({
    address: Yup.string(),
    googlePlaceId: Yup.string(),
  }).test(
    "address-required",
    i18next.t("sites.errorPlace"),
    (value?: Partial<AutocompleteFieldValue>) => Boolean(value?.address)
  ),
  country: Yup.string().required(i18next.t("validations.country")),
  comment: Yup.string(),
});
